import { useState } from 'react';
import { Box, Button, Chip } from '@mui/material';
import { useNotify } from 'react-admin';
import { useImpersonation } from './ImpersonationContext';

/**
 * The persistent, unmissable impersonation indicator (D-impersonation). Lives in the global
 * AppBar, so while a session is ACTIVE it renders on EVERY route: a warning-styled
 * "Viewing as <member>" chip the agent cannot miss. It offers ONLY the end-session action
 * and nothing else. When the session ends (explicit) or expires (derived off the clock), the
 * active session clears and the indicator disappears.
 */
export const ImpersonationIndicator = () => {
  const { activeSession, end } = useImpersonation();
  const notify = useNotify();
  const [ending, setEnding] = useState(false);

  if (!activeSession) return null;

  const onEnd = async () => {
    setEnding(true);
    try {
      await end();
      notify('Impersonation session ended', { type: 'info' });
    } catch {
      notify('Could not end the session', { type: 'error' });
    } finally {
      setEnding(false);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}
      data-testid="impersonation-indicator"
    >
      <Chip
        color="warning"
        variant="filled"
        label={`Viewing as ${activeSession.subjectName}`}
        data-testid="impersonation-indicator-label"
      />
      <Button
        size="small"
        color="inherit"
        variant="outlined"
        disabled={ending}
        onClick={() => void onEnd()}
      >
        End session
      </Button>
    </Box>
  );
};
